/**
 * "What happens when you approve": the dry run of a pending change request,
 * shown above the Approve button. It lists in plain words what one click will
 * create or change, what needs attention, and what stops the approve. For a
 * group change it also lets the reviewer choose the target group (saved on the
 * request before approving).
 */

import { useLingui } from '@lingui/react';
import { AlertTriangle, CheckCircle2, Loader2, XCircle } from 'lucide-react';

import type { change_requests_ApprovalPreviewResponse } from '@/api';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { previewLineText, type PreviewLine, type PreviewTextContext } from './approvalPreviewText';

export interface GroupOption {
  id: string;
  name: string;
}

interface ApprovalOutcomePanelProps {
  preview: change_requests_ApprovalPreviewResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  textContext: Omit<PreviewTextContext, 'i18n'>;
  /** Group change only: the groups to choose from and the current choice. */
  groupChoice?: {
    options: GroupOption[];
    value: string;
    onChange: (groupId: string) => void;
    saving: boolean;
  };
}

/** The lines of one section as a list with an icon per line. */
function Lines({ lines, tone, ctx }: { lines: PreviewLine[]; tone: 'action' | 'warning' | 'blocker'; ctx: PreviewTextContext }) {
  const Icon = tone === 'action' ? CheckCircle2 : tone === 'warning' ? AlertTriangle : XCircle;
  const color =
    tone === 'action' ? 'text-success-on-surface' : tone === 'warning' ? 'text-warning-on-surface' : 'text-destructive';
  return (
    <ul className="space-y-1.5" data-testid={`approval-preview-${tone}`}>
      {lines.map((line, idx) => (
        <li key={`${line.code}-${idx}`} className="flex items-start gap-2 text-sm">
          <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${color}`} aria-hidden="true" />
          <span className="break-words">{previewLineText(line, ctx)}</span>
        </li>
      ))}
    </ul>
  );
}

export function ApprovalOutcomePanel({ preview, isLoading, isError, textContext, groupChoice }: ApprovalOutcomePanelProps) {
  const { i18n } = useLingui();
  const ctx: PreviewTextContext = { i18n, ...textContext };

  return (
    <section
      className="space-y-3 rounded-md border bg-muted/30 p-3"
      aria-labelledby="approval-outcome-title"
      data-testid="approval-outcome"
    >
      <h4 id="approval-outcome-title" className="text-sm font-medium">
        {i18n._({ id: 'approvalPreview.title', message: 'What happens when you approve' })}
      </h4>

      {groupChoice && (
        <div className="space-y-1">
          <Label htmlFor="approval-target-group">
            {i18n._({ id: 'approvalPreview.targetGroup', message: 'Target group' })}
          </Label>
          <Select value={groupChoice.value} onValueChange={groupChoice.onChange} disabled={groupChoice.saving}>
            <SelectTrigger id="approval-target-group" data-testid="approval-target-group">
              <SelectValue
                placeholder={i18n._({ id: 'approvalPreview.targetGroupPlaceholder', message: 'Choose a group' })}
              />
            </SelectTrigger>
            <SelectContent>
              {groupChoice.options.map((group) => (
                <SelectItem key={group.id} value={group.id}>
                  {group.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {isLoading && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          {i18n._({ id: 'approvalPreview.loading', message: 'Checking what the approval will change...' })}
        </p>
      )}

      {isError && (
        <p className="text-sm text-muted-foreground" role="status">
          {i18n._({
            id: 'approvalPreview.error',
            message: 'The preview could not be loaded. You can still approve; the result is shown afterwards.',
          })}
        </p>
      )}

      {preview && (
        <>
          {preview.actions.length > 0 && <Lines lines={preview.actions} tone="action" ctx={ctx} />}
          {preview.warnings.length > 0 && <Lines lines={preview.warnings} tone="warning" ctx={ctx} />}
          {preview.blockers.length > 0 && (
            <div className="space-y-1.5">
              <Lines lines={preview.blockers} tone="blocker" ctx={ctx} />
              <p className="text-xs text-muted-foreground">
                {i18n._({
                  id: 'approvalPreview.blockedHint',
                  message: 'Approving is not possible yet. Fix the point above or reject the request.',
                })}
              </p>
            </div>
          )}
        </>
      )}
    </section>
  );
}
